import React, { useEffect, useState } from 'react';

const ZKAS_ADDRESS = 'zkas:px34dgawfhyrr6xtelm52fae476rcrfqs5q0ex9w62wft9hytlf3rn79nm8cd9acxnthl2cp7tkqqj8';

const formatHashrate = (hashrate) => {
  const value = Number(hashrate) || 0;
  const units = ['H/s', 'KH/s', 'MH/s', 'GH/s', 'TH/s', 'PH/s'];
  let scaled = value;
  let unitIndex = 0;

  while (scaled >= 1000 && unitIndex < units.length - 1) {
    scaled /= 1000;
    unitIndex += 1;
  }

  return `${scaled.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${units[unitIndex]}`;
};

const formatAmount = (amount) => Number(amount || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const formatPayoutHash = (hash) => hash ? `${hash.slice(0, 10)}...${hash.slice(-6)}` : '-';

const ZkasMinerCard = ({ styles, isMobile, getExpectedHashrate, isDetailsTab = false }) => {
  const [miner, setMiner] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchMiner = async () => {
      try {
        const isLocal = ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname)
          || window.location.hostname.endsWith('.local');
        const apiUrl = isLocal ? '/api-local/zkas/miner' : '/.netlify/functions/zkas-miner';
        const requestUrl = `${apiUrl}?address=${encodeURIComponent(ZKAS_ADDRESS)}`;
        console.debug('[zkas] requesting miner data:', requestUrl);
        const response = await fetch(requestUrl, {
          cache: 'no-store'
        });
        const contentType = response.headers.get('content-type') || 'unknown';
        const responseText = await response.text();
        console.debug('[zkas] response:', {
          status: response.status,
          contentType,
          preview: responseText.slice(0, 200)
        });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status} from ${requestUrl} (${contentType}): ${responseText.slice(0, 120)}`);
        }

        let data;
        try {
          data = JSON.parse(responseText);
        } catch (parseError) {
          console.error('[zkas] expected JSON but received:', {
            requestUrl,
            status: response.status,
            contentType,
            preview: responseText.slice(0, 500),
            parseError
          });
          throw new Error(`Expected JSON from ${requestUrl}, received ${contentType}. Response starts: ${responseText.slice(0, 120)}`);
        }
        if (!cancelled) {
          setMiner(data.found ? data : null);
          setError(data.found ? null : 'No miner found for this address.');
        }
      } catch (fetchError) {
        console.error('[zkas] miner lookup failed:', fetchError);
        if (!cancelled) setError(fetchError.message);
      }
    };

    fetchMiner();
    const interval = setInterval(fetchMiner, 10000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const workers = miner?.workers || [];
  const payouts = miner?.payouts || [];
  const recentPayouts = payouts.filter((payout) => {
    const timestamp = Number(payout.ts) || 0;
    const payoutTime = timestamp > 1e12 ? timestamp : timestamp * 1000;
    return payoutTime >= Date.now() - (24 * 60 * 60 * 1000);
  });
  const getExpected = (workerName) => {
    const powerPoolName = workerName === 'Neville'
      ? 'ks02'
      : workerName === 'Ronaldo'
        ? 'ks07'
        : workerName === 'Irwin'
          ? 'ks03'
          : workerName;
    return getExpectedHashrate?.(powerPoolName) || 0;
  };
  const getWorkerHashrateColor = (worker) => {
    const expected = getExpected(worker.worker);
    if (!expected) return '#22c55e';
    const hashrateTH = ((Number(worker.hashrate) || 0) * 1e9) / 1e12;
    if (hashrateTH < 0.5 * expected) return '#ef4444';
    if (hashrateTH < 0.85 * expected) return '#f59e42';
    if (hashrateTH < expected) return '#e2e8f0';
    return '#22c55e';
  };
  const expectedTotalHashrate = workers.reduce((total, worker) => total + getExpected(worker.worker), 0);
  const currentTotalHashrate = (Number(miner?.totalHashrate) || 0) / 1000;
  const totalHashrateRatio = expectedTotalHashrate > 0 ? currentTotalHashrate / expectedTotalHashrate : 0;
  const totalHashrateColor = expectedTotalHashrate === 0
    ? '#fff'
    : totalHashrateRatio >= 0.95
      ? '#22c55e'
      : totalHashrateRatio >= 0.8
        ? '#f59e42'
        : '#ef4444';

  return (
    <div className="column is-12" style={!isMobile ? { display: 'flex', flexDirection: 'column' } : {}}>
      <div className="box" style={!isMobile ? { ...styles.statCard, flex: 1 } : styles.statCard}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
          <div>
            <p className="heading" style={styles.statLabel}>ZKas Pool</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <a
              href="https://mining-pool.zkas.info"
              target="_blank"
              rel="noopener noreferrer"
              className="button is-info is-small"
              onClick={() => {
                if (!navigator.clipboard) {
                  console.warn('[zkas] Clipboard API unavailable');
                  return;
                }
                navigator.clipboard.writeText(ZKAS_ADDRESS)
                  .catch((copyError) => console.warn('[zkas] Failed to copy address:', copyError));
              }}
            >
              View pool
            </a>
          </div>
        </div>

        {error && <p style={{ ...styles.poolLabel, color: '#ef4444' }}>{error}</p>}
        {!miner && !error && <p style={styles.poolLabel}>Loading miner stats...</p>}

        {miner && (
          <>
            <div className="columns is-multiline" style={{ marginTop: '0.25rem' }}>
              {[
                ['Your hashrate', formatHashrate((miner.totalHashrate || 0) * 1e9), totalHashrateColor],
                ['ZKAS blocks', (miner.blocksFound || 0).toLocaleString()],
                ['KAS blocks', miner.kasPayoutSet === false ? 'Not set' : (miner.kasBlocksFound || 0).toLocaleString()],
                ['Paid', `${formatAmount(miner.paidFc)} $zkas`]
              ].map(([label, value, valueColor]) => (
                <div className="column is-2-desktop is-4-tablet is-6-mobile" key={label}>
                  <div style={{ ...styles.statCard, padding: '0.75rem', height: '100%' }}>
                    <p style={{ ...styles.statValue, color: valueColor || styles.statValue?.color }}>{value}</p>
                    <p style={styles.statLabel}>{label}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="table-container" style={{ marginTop: '0.5rem' }}>
              <table className="table is-fullwidth is-hoverable" style={styles.table}>
                <thead><tr>{['Worker', 'Hashrate', 'Status', 'Blocks', 'KAS'].map(heading => <th key={heading} style={styles.tableHeader}>{heading}</th>)}</tr></thead>
                <tbody>
                  {workers.length > 0 ? workers.map(worker => (
                    <tr key={worker.worker}>
                      <td style={styles.tableCell}>{worker.worker || '-'}</td>
                      <td style={{ ...styles.tableCell, color: getWorkerHashrateColor(worker) }}>{formatHashrate((worker.hashrate || 0) * 1e9)}</td>
                      <td style={{ ...styles.tableCell, color: worker.online ? '#22c55e' : '#ef4444' }}>{worker.warmingUp ? 'Waiting for shares' : worker.online ? 'Mining' : 'Offline'}</td>
                      <td style={styles.tableCell}>{(worker.blocks || 0).toLocaleString()}</td>
                      <td style={styles.tableCell}>{worker.kasPayoutSet === false ? 'Not set' : (worker.kasBlocks || 0).toLocaleString()}</td>
                    </tr>
                  )) : <tr><td colSpan="5" style={styles.tableCell}>No active workers.</td></tr>}
                </tbody>
              </table>
            </div>

            {isDetailsTab && (
              <>
                <h3 className="title is-6" style={{ ...styles.tableTitle, marginTop: '1rem' }}>Payout history</h3>
                <div className="table-container">
                  <table className="table is-fullwidth is-hoverable" style={styles.table}>
                    <thead><tr>{['When', 'Worker', 'Amount', 'Block'].map(heading => <th key={heading} style={styles.tableHeader}>{heading}</th>)}</tr></thead>
                    <tbody>
                      {recentPayouts.length > 0 ? recentPayouts.map((payout, index) => (
                        <tr key={`${payout.hash || 'payout'}-${index}`}>
                          <td style={styles.tableCell}>{payout.ts ? new Date((Number(payout.ts) > 1e12 ? Number(payout.ts) : Number(payout.ts) * 1000)).toLocaleString('en-GB') : '-'}</td>
                          <td style={styles.tableCell}>{payout.worker || '-'}</td>
                          <td style={styles.tableCell}>{payout.amountFc == null ? '-' : `${formatAmount(payout.amountFc)} $zkas`}</td>
                          <td style={{ ...styles.tableCell, fontFamily: 'monospace', fontSize: '0.85em' }}>{formatPayoutHash(payout.hash)}</td>
                        </tr>
                      )) : <tr><td colSpan="4" style={styles.tableCell}>No payouts in the last 24 hours.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ZkasMinerCard;